import { SupportedTimeZones } from 'enums';
import spacetime, { TimezoneMeta } from 'spacetime';
import countriesTimeZones, { Country } from 'countries-and-timezones';
import soft from 'timezone-soft';

import { UKRAINE_COUNTRY_CODE, UKRAINE_COUNTRY_NAME } from 'consts';

// this is needed because spacetime has deprecated time zone name for Kyiv. Sometimes we want to convert 'Europe/Kyiv' => 'Europe/Kiev' and 'Europe/Kiev' => 'Europe/Kyiv',
enum TimeZoneNameMapping {
  'Europe/Kiev' = 'Europe/Kyiv',
  'Europe/Kyiv' = 'Europe/Kiev',
  'Europe/Zaporozhye' = 'Europe/Zaporizhzhia',
  'Europe/Zaporizhzhia' = 'Europe/Zaporozhye',
}

export interface TimeZoneInfo {
  name: string;
  abbr: string;
}

export interface DisplayFormat {
  iana: string;
  standard: TimeZoneInfo;
  daylight: TimeZoneInfo;
}

// this module was created to override some defaults of spacetime lib
class TimeZoneMappingService {
  // since spacetime has wrong and outdated info about Kyiv  we need to patch the package a bit
  // this will be used only by spacetime library
  mapCorrectTimeZoneDataToDeprecated(timeZoneName: SupportedTimeZones) {
    const spacetimeDate = spacetime();
    return spacetimeDate.goto(this.mapTimeZoneNames(timeZoneName));
  }

  // countries-and-timezones lib has wrong understanding about Europe/Simferopol time zone and part of which country it is
  getCorrectCountryDataByTimeZone(timeZoneName: SupportedTimeZones) {
    const countryData = countriesTimeZones.getCountryForTimezone(
      this.mapTimeZoneNames(timeZoneName)
    ) as Country;

    if (timeZoneName === SupportedTimeZones['Europe/Simferopol']) {
      countryData.id = UKRAINE_COUNTRY_CODE;
      countryData.name = UKRAINE_COUNTRY_NAME;
    }

    return countryData;
  }

  // countries-and-timezones lib has wrong understanding about Europe/Simferopol time zone and part of which country it is
  getCorrectCountriesDataByTimeZone(timeZoneName: SupportedTimeZones) {
    const countriesData = countriesTimeZones.getCountriesForTimezone(
      this.mapTimeZoneNames(timeZoneName)
    ) as Country[];

    return countriesData.filter((country) => {
      return !(
        timeZoneName === SupportedTimeZones['Europe/Simferopol'] &&
        country.id !== 'UA'
      );
    });
  }

  guessUsersTimeZone(): SupportedTimeZones | null {
    try {
      const { name } = spacetime().timezone();

      const mappedTzName = this.mapTimeZoneNames(name);

      if (!(mappedTzName in SupportedTimeZones)) return null;

      return mappedTzName as SupportedTimeZones;
    } catch {
      return null;
    }
  }

  getSoftTzAbbreviations(timeZoneName: SupportedTimeZones) {
    const mappedTzNameToDeprecated =
      timeZoneMappingService.mapTimeZoneNames(timeZoneName);

    const [softMatch] =
      (soft(mappedTzNameToDeprecated) as unknown as DisplayFormat[]) || [];

    if (!softMatch || (!softMatch.daylight && !softMatch.standard)) return [];

    return (
      [softMatch.daylight?.abbr, softMatch.standard?.abbr]
        // for some reason sometimes the abbreviation is returned as UTC offset, we do not need such results
        .filter(Boolean) as string[]
    );
  }

  getSoftTimeZoneName({ name, current }: TimezoneMeta) {
    const [softMatch] = (soft(name) as unknown as DisplayFormat[]) || [];

    if (!softMatch || (!softMatch.daylight && !softMatch.standard)) {
      return null;
    }

    // defaults to standard abbr if there's mismatch
    const result =
      current.isDST && softMatch.daylight
        ? softMatch.daylight.name
        : softMatch.standard.name;

    return result || null;
  }

  // since spacetime has wrong and outdated info about Kyiv  we need to patch the package a bit
  // this will be used only by spacetime library
  mapTimeZoneNames(timeZoneName: string) {
    return (
      (TimeZoneNameMapping as { [key: string]: string })[timeZoneName] ??
      timeZoneName
    );
  }
}

export const timeZoneMappingService = new TimeZoneMappingService();
